<template>
  <div class="col-12 col-md-12 col-lg-8 col-xl-8 mb-3">
    <div class="row gallery-row mb-2">
      <!-- Desktop: Large image display -->
      <div class="col col-12 mb-2 d-none d-md-block">
        <img
          :src="images[selectedIndex].url || images[selectedIndex]"
          class="img-fluid"
          :alt="images[selectedIndex].alt || generateAltText(selectedIndex)"
        >
      </div>
      
      <!-- Mobile: Horizontal scrolling gallery -->
      <div class="d-block d-md-none mobile-gallery-container">
        <div 
          class="mobile-gallery-scroll"
          @touchstart="handleTouchStart"
          @touchmove="handleTouchMove"
          @touchend="handleTouchEnd"
        >
          <div
            v-for="(image, index) in images"
            :key="(image.url || image) + '_mobile_' + index"
            class="mobile-gallery-item"
            @click="selectedIndex = index"
          >
            <img
              :src="image.url || image"
              class="img-fluid"
              :alt="image.alt || generateAltText(index)"
              :class="{ 'active': index === selectedIndex }"
            >
          </div>
        </div>
        <!-- Navigation arrows for mobile -->
        <button 
          v-if="images.length > 1"
          class="mobile-gallery-arrow mobile-gallery-arrow-left"
          @click="scrollLeft"
          :disabled="scrollPosition <= 0"
        >
          <i class="fas fa-chevron-left"></i>
        </button>
        <button 
          v-if="images.length > 1"
          class="mobile-gallery-arrow mobile-gallery-arrow-right"
          @click="scrollRight"
          :disabled="scrollPosition >= maxScrollPosition"
        >
          <i class="fas fa-chevron-right"></i>
        </button>
      </div>
      
      <!-- Desktop: Thumbnail gallery -->
      <div
        v-for="(image, index) in images"
        :key="(image.url || image) + '_ ' + index"
        class="col col-12 col-sm-4 mb-2 gallery-image d-none d-md-block"
        @click="selectedIndex = index"
      >
        <img
          :src="image.url || image"
          class="img-fluid"
          :alt="image.alt || generateAltText(index)"
        >
      </div>
    </div>
  </div>
</template>

<script>
  import Vue from 'vue';
  import {Component, Prop} from 'vue-property-decorator';

  @Component
  export default class TypeImages extends Vue {
    selectedIndex = 0;
    scrollPosition = 0;
    touchStartX = 0;
    touchStartY = 0;
    isSwiping = false;
    
    @Prop(Array) images
    @Prop(Object) type // Car type data for alt text generation
    @Prop(Object) make // Brand data for alt text generation

    get maxScrollPosition() {
      if (!this.images || this.images.length <= 1) return 0;
      return (this.images.length - 1) * 250;
    }

    scrollLeft() {
      if (this.scrollPosition > 0) {
        this.scrollPosition = Math.max(0, this.scrollPosition - 250);
        this.updateScrollPosition();
      }
    }

    scrollRight() {
      if (this.scrollPosition < this.maxScrollPosition) {
        this.scrollPosition = Math.min(this.maxScrollPosition, this.scrollPosition + 250);
        this.updateScrollPosition();
      }
    }

    updateScrollPosition() {
      const scrollContainer = this.$el.querySelector('.mobile-gallery-scroll');
      if (scrollContainer) {
        scrollContainer.style.transform = `translateX(-${this.scrollPosition}px)`;
      }
    }

    handleTouchStart(event) {
      this.touchStartX = event.touches[0].clientX;
      this.touchStartY = event.touches[0].clientY;
      this.isSwiping = false;
    }

    handleTouchMove(event) {
      if (!this.touchStartX || !this.touchStartY) return;
      
      const touchX = event.touches[0].clientX;
      const touchY = event.touches[0].clientY;
      const diffX = this.touchStartX - touchX;
      const diffY = this.touchStartY - touchY;
      
      // Check if horizontal swipe is more significant than vertical
      if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 10) {
        this.isSwiping = true;
        event.preventDefault(); // Prevent scrolling
      }
    }

    handleTouchEnd(event) {
      if (!this.isSwiping || !this.touchStartX) return;
      
      const touchEndX = event.changedTouches[0].clientX;
      const diffX = this.touchStartX - touchEndX;
      
      // Swipe threshold
      if (Math.abs(diffX) > 50) {
        if (diffX > 0) {
          // Swiped left - go to next image
          this.scrollRight();
        } else {
          // Swiped right - go to previous image
          this.scrollLeft();
        }
      }
      
      // Reset touch values
      this.touchStartX = 0;
      this.touchStartY = 0;
      this.isSwiping = false;
    }

    generateAltText(index) {
      if (!this.type && !this.make) {
        return 'Autó kép';
      }
      
      const imageNumber = index + 1;
      const makeName = this.type?.model?.make?.name || this.make?.name || 'Autó';
      const modelName = this.type?.model?.MODNameGrp1 || '';
      const bodyType = this.type?.body_type_name || '';
      
      // Generate alt text based on image index
      switch (imageNumber) {
        case 1:
          return `${makeName} ${modelName} ${bodyType} operatív lízing`;
        case 2:
          return `${makeName} ${modelName} ${bodyType} tartós bérlet`;
        case 3:
          return `${makeName} ${modelName} ${bodyType} flottakezelés`;
        default:
          return `${makeName} ${modelName} ${bodyType} autólízing`;
      }
    }
  }
</script>

<style scoped>
  .gallery-image {
    cursor: pointer;
  }

  /* Mobile gallery styles */
  .mobile-gallery-container {
    position: relative;
    width: 100%;
    overflow: hidden;
    padding: 0 15px; /* Add padding to match page margins */
  }

  .mobile-gallery-scroll {
    display: flex;
    transition: transform 0.3s ease;
    width: calc(260px * 3 + 20px * 2); /* Exactly 3 images with 2 gaps */
    touch-action: pan-y;
  }

  .mobile-gallery-item {
    flex: 0 0 260px;
    cursor: pointer;
  }

  .mobile-gallery-item:not(:last-child) {
    margin-right: 20px;
  }

  .mobile-gallery-item img {
    width: 100%;
    height: 180px;
    object-fit: cover;
    border-radius: 8px;
    border: none; /* Remove border completely */
    transition: opacity 0.3s ease;
  }

  .mobile-gallery-arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background: rgba(0, 0, 0, 0.7);
    color: white;
    border: none;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 10;
    transition: background-color 0.3s ease;
  }

  .mobile-gallery-arrow:hover:not(:disabled) {
    background: rgba(0, 0, 0, 0.9);
  }

  .mobile-gallery-arrow:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  .mobile-gallery-arrow-left {
    left: 10px;
  }

  .mobile-gallery-arrow-right {
    right: 10px;
  }
</style>
